import { AlsoIn } from "../../tasks/AlsoIn";
import { setCounterWorkspaces } from "../../core/links";
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Minus, Pencil, Plus } from "lucide-react";
import { clsx } from "clsx";
import { registerViewType, type ViewProps } from "../../registry/viewTypes";
import { useDocSelector } from "../../core/useDocSelector";
import { C } from "../../core/schema";
import type { FilterAst } from "../../core/filter";
import { Button, Field, Floating, Input, Select, Why } from "../../ui";
import { useSettings } from "../../app/settings";
import { useCircle } from "../../circle/useCircle";
import { dayCounts, dayKey, recurringDoneDays, summarize, viewDays } from "../../core/habits";
import { CounterTiles } from "../counters/CountersView";
import { FILTER_SCHEMA, visibleTasks } from "../shared";
import {
  createCounter,
  setCounterValue,
  updateCounter,
  type Counter,
  type CounterKind,
  type CounterPeriod,
  valuesOf,
  type CounterScope,
} from "../counters/ops";

export interface HabitsConfig {
  /** a grid of days (default) or tiles with today's count */
  display?: "grid" | "tiles";
  /** tiles: only these counters (default: all) */
  counterIds?: string[];
  range?: "week" | "month";
  /** show soft recurring tasks as rows too (read-only: their done days) */
  showRecurring?: boolean;
  /** which recurring tasks, when shown */
  filter?: FilterAst;
}

type Row =
  | { kind: "habit"; id: string; habit: Counter }
  | { kind: "task"; id: string; title: string; done: Set<string> };

/**
 * Habits (spec §9.7 `habits`, §9.19 streak-free): yes/no and counted habits as a week or month grid, plus
 * optionally recurring tasks. Past days stay editable ("forgot to tick it yesterday"). Empty days are just
 * empty — no crosses, no red, no streaks; the summary says what happened, never what did not.
 * A widget too narrow for the whole week or month shows as many days as fit, and ‹ › move through the rest
 * before going to the previous or next week or month: it never scrolls sideways.
 */
/**
 * *Habits and counts* (UI cleanup U2): one widget over every counter, shown as a grid of days (yes or no, or a
 * number with an optional goal) or as tiles with today's count and − / +. It replaces the separate Habits and
 * Counters widgets, which split one store by a flag and had buttons to move a counter between them.
 */
export function HabitsView(props: ViewProps<HabitsConfig>) {
  return props.config.display === "tiles" ? (
    <CounterTiles ctx={props.ctx} counterIds={props.config.counterIds} />
  ) : (
    <HabitsGrid {...props} />
  );
}

function HabitsGrid({ config, ctx }: ViewProps<HabitsConfig>) {
  const { t, i18n } = useTranslation();
  const settings = useSettings(ctx.handle);
  // a per-person habit shows my own values only (ADR 0024); the switch is offered in a shared workspace
  const { shared, me } = useCircle(ctx.handle);
  const range = config.range ?? "week";
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState<string | null>(null);
  const [counting, setCounting] = useState<string | null>(null); // "<id>|<day>" with the +/− open
  const stepAnchor = useRef<HTMLButtonElement | null>(null);
  // the +/− closes on a press outside it or Escape, like every pop-up (it would cover the row below)
  useEffect(() => {
    if (!counting) return;
    const onDown = (e: PointerEvent) => {
      const el = e.target as Element | null;
      if (!el?.closest("[data-testid=habit-stepper]") && !el?.closest("[data-counting=true]"))
        setCounting(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setCounting(null);
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [counting]);
  const today = useMemo(() => new Date(Math.floor(ctx.now / 60_000) * 60_000), [ctx.now]);
  const todayKey = dayKey(today);
  const days = useMemo(() => viewDays(today, range, offset), [today, range, offset]);
  // how many day columns fit: the widget's width minus the name and summary columns, 24 px a day
  const root = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = root.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth);
    measure();
    const obs = new ResizeObserver(measure);
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  // the container widths of the classes below: @lg (32 rem) gives the name 12 rem, @sm (24 rem) 8 rem, below 6 rem;
  // below @sm the summary sits under the name instead of in a column of its own
  const nameW = width >= 512 ? 200 : width >= 384 ? 136 : 104;
  const sumW = width >= 384 ? 112 : 0;
  // name and summary with their 8 px gaps, the widget's padding 32 px and 8 px to spare; 24 px a day
  const fit = width ? Math.max(1, Math.floor((width - 40 - nameW - sumW) / 24)) : days.length;
  // even parts: a week that does not fit is 4 + 3 days, a month 16 + 15, not 6 + 1 or 23 + 8
  const pages = Math.ceil(days.length / Math.min(days.length, fit));
  const per = Math.ceil(days.length / pages);
  // null: the part holding today (or the first); -1: the last part (after stepping back from the next range)
  const [part, setPart] = useState<number | null>(null);
  const todayAt = days.findIndex((d) => dayKey(d) === todayKey);
  const page = Math.min(
    pages - 1,
    part === -1 ? pages - 1 : (part ?? (todayAt >= 0 ? Math.floor(todayAt / per) : 0)),
  );
  const shown = days.slice(page * per, page * per + per);
  const earlier = () => {
    if (page > 0) setPart(page - 1);
    else {
      setOffset(offset - 1);
      setPart(-1);
    }
  };
  const later = () => {
    if (page < pages - 1) setPart(page + 1);
    else {
      setOffset(offset + 1);
      setPart(0);
    }
  };
  const atToday = offset === 0 && (todayAt < 0 || Math.floor(todayAt / per) === page);
  const habits =
    useDocSelector(ctx.handle, (doc) => doc.getMap(C.counters).toJSON() as Record<string, Counter>) ?? {};
  const recurring =
    useDocSelector(
      ctx.handle,
      (doc) =>
        config.showRecurring
          ? visibleTasks(doc, config.filter, Date.now(), { includeDone: true })
              .tasks.filter((x) => x.typeId === "recurring")
              .map((x) => ({ id: x.id, title: x.title, props: x.props }))
          : [],
      [config],
    ) ?? [];
  const rows: Row[] = [
    ...Object.entries(habits)
      .filter(([, h]) => h && typeof h === "object" && !h.archivedAt)
      .map(([id, h]) => ({ kind: "habit" as const, id, habit: { ...h, entries: valuesOf(h, me) } })),
    ...recurring.map((r) => ({
      kind: "task" as const,
      id: r.id,
      title: r.title,
      done: recurringDoneDays(r.props),
    })),
  ];
  const ro = ctx.readOnly;
  const fmtDay = new Intl.DateTimeFormat(i18n.language, { weekday: "short", day: "numeric" });
  const fmtLong = new Intl.DateTimeFormat(i18n.language, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
  const rangeLabel =
    pages > 1
      ? `${fmtLong.format(shown[0])} – ${fmtLong.format(shown[shown.length - 1])}`
      : range === "week"
        ? `${fmtLong.format(days[0])} – ${fmtLong.format(days[6])}`
        : new Intl.DateTimeFormat(i18n.language, { month: "long", year: "numeric" }).format(days[0]);

  const cellTone = (h: Counter, v: number) =>
    dayCounts(h, v)
      ? "bg-accent text-white dark:text-neutral-950"
      : v > 0
        ? "bg-accent/30 text-accent-ink"
        : "bg-[var(--surface-2)]";

  const summary = (h: Counter, testId = "habit-summary") => {
    if (settings.a11y.muteBadges) return null; // counts are opt-in to look at
    const s = summarize(h, days, today);
    const text = s.week
      ? t("habits.weekGoal", { done: s.week.done, goal: s.week.goal })
      : t(range === "week" ? "habits.daysThisWeek" : "habits.daysThisMonth", { n: s.days });
    return (
      <span
        className="inline-flex max-w-[6.5rem] flex-wrap items-center justify-end gap-x-1 text-[12px] text-neutral-500"
        data-testid={testId}
      >
        {text}
        <Why
          title={h.title}
          rows={
            s.counted.length
              ? s.counted.map((k) => [fmtLong.format(new Date(`${k}T12:00:00`)), String(h.entries[k] ?? "")])
              : [[t("habits.noneYet"), ""]]
          }
        />
      </span>
    );
  };

  return (
    <div
      ref={root}
      className="kss-surface @container flex h-full flex-col gap-3 overflow-auto p-4"
      data-testid="habits-view"
      data-days={shown.length}
    >
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          aria-label={t("habits.earlier")}
          onClick={earlier}
          data-testid="habits-earlier"
          className="w-[28px] px-0"
        >
          <ChevronLeft size={14} aria-hidden />
        </Button>
        <span className="min-w-[10rem] text-center text-[12.5px]">{rangeLabel}</span>
        <Button
          variant="ghost"
          size="sm"
          aria-label={t("habits.later")}
          onClick={later}
          disabled={offset >= 0 && page >= pages - 1}
          data-testid="habits-later"
          className="w-[28px] px-0"
        >
          <ChevronRight size={14} aria-hidden />
        </Button>
        {!atToday && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setOffset(0);
              setPart(null);
            }}
          >
            {t(range === "week" ? "habits.thisWeek" : "habits.thisMonth")}
          </Button>
        )}
      </div>

      {rows.length === 0 ? (
        <p className="text-[13px] text-neutral-500">{t("habits.empty")}</p>
      ) : (
        <table className="w-full border-separate border-spacing-y-1 text-[12.5px]" data-testid="habits-grid">
          <thead>
            <tr>
              <th className="text-left font-normal text-neutral-500" />
              {shown.map((d) => (
                <th
                  key={dayKey(d)}
                  scope="col"
                  className={clsx(
                    "px-0.5 text-center font-normal text-neutral-500",
                    dayKey(d) === todayKey && "text-accent",
                  )}
                >
                  {range === "week" ? fmtDay.format(d) : d.getDate()}
                </th>
              ))}
              <th className="hidden @sm:table-cell" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) =>
              row.kind === "task" ? (
                <tr key={row.id} data-testid="habit-row" data-kind="task">
                  <th scope="row" className="pr-2 text-left font-normal">
                    <button
                      type="button"
                      className="block max-w-[6rem] truncate text-left hover:text-accent @sm:max-w-[8rem] @lg:max-w-[12rem]"
                      onClick={() => ctx.openTask(row.id)}
                    >
                      {row.title}
                    </button>
                  </th>
                  {shown.map((d) => {
                    const k = dayKey(d);
                    const done = row.done.has(k);
                    return (
                      <td key={k} className="px-0.5 text-center">
                        <span
                          className={clsx(
                            "inline-block h-6 w-5 rounded-[5px] align-middle",
                            done ? "bg-accent" : "bg-[var(--surface-2)]",
                          )}
                          aria-label={t(done ? "habits.cellDone" : "habits.cellOpen", {
                            title: row.title,
                            day: fmtLong.format(d),
                          })}
                          role="img"
                        />
                      </td>
                    );
                  })}
                  <td className="hidden @sm:table-cell" />
                </tr>
              ) : (
                <Fragment key={row.id}>
                  <tr
                    data-testid="habit-row"
                    data-habit={row.habit.title}
                    data-kind={row.habit.kind ?? "count"}
                  >
                    <th scope="row" className="pr-2 text-left font-normal">
                      <span className="flex max-w-[6rem] items-center gap-1 @sm:max-w-[8rem] @lg:max-w-[12rem]">
                        <span className="truncate">{row.habit.title}</span>
                        {!ro && (
                          <button
                            type="button"
                            className="text-neutral-400 hover:text-accent"
                            aria-label={t("habits.editLabel", { title: row.habit.title })}
                            onClick={() => setEditing(editing === row.id ? null : row.id)}
                          >
                            <Pencil size={12} aria-hidden />
                          </button>
                        )}
                      </span>
                      {/* a narrow widget: the summary under the name, the days get the room */}
                      <span className="block @sm:hidden">{summary(row.habit, "habit-summary-narrow")}</span>
                    </th>
                    {shown.map((d) => {
                      const k = dayKey(d);
                      const v = row.habit.entries[k] ?? 0;
                      const future = k > todayKey;
                      const label =
                        row.habit.kind === "check"
                          ? t(v > 0 ? "habits.cellDone" : "habits.cellOpen", {
                              title: row.habit.title,
                              day: fmtLong.format(d),
                            })
                          : t("habits.cellCount", {
                              title: row.habit.title,
                              day: fmtLong.format(d),
                              n: v,
                              unit: row.habit.unit ?? "",
                            });
                      const key = `${row.id}|${k}`;
                      return (
                        <td key={k} className="relative px-0.5 text-center">
                          <button
                            type="button"
                            disabled={ro || future}
                            aria-label={label}
                            aria-pressed={row.habit.kind === "check" ? v > 0 : undefined}
                            data-testid="habit-cell"
                            data-day={k}
                            data-counting={counting === key ? "true" : undefined}
                            ref={counting === key ? stepAnchor : undefined}
                            className={clsx(
                              "h-6 w-5 rounded-[5px] align-middle font-mono text-[10.5px] disabled:opacity-40",
                              cellTone(row.habit, v),
                              k === todayKey && "ring-1 ring-accent/50",
                            )}
                            onClick={() => {
                              if (row.habit.kind === "check")
                                ctx.write((doc) => setCounterValue(doc, row.id, k, v > 0 ? 0 : 1));
                              else setCounting(counting === key ? null : key);
                            }}
                          >
                            {row.habit.kind !== "check" && v > 0 ? v : ""}
                          </button>
                          {counting === key && (
                            // above the page, so the widget's edge never cuts it off
                            <Floating
                              anchor={stepAnchor}
                              align="center"
                              className="flex items-center gap-1 rounded-md border border-neutral-200 bg-[var(--surface)] p-1 text-[12.5px] shadow-[var(--shadow-md)] dark:border-neutral-700"
                              data-testid="habit-stepper"
                            >
                              <button
                                type="button"
                                aria-label={t("habits.less")}
                                className="grid h-6 w-6 place-items-center rounded-sm hover:bg-neutral-100 dark:hover:bg-neutral-800"
                                onClick={() => ctx.write((doc) => setCounterValue(doc, row.id, k, v - 1))}
                              >
                                <Minus size={12} aria-hidden />
                              </button>
                              <span className="min-w-[1.5rem] font-mono">{v}</span>
                              <button
                                type="button"
                                aria-label={t("habits.more")}
                                className="grid h-6 w-6 place-items-center rounded-sm hover:bg-neutral-100 dark:hover:bg-neutral-800"
                                onClick={() => ctx.write((doc) => setCounterValue(doc, row.id, k, v + 1))}
                              >
                                <Plus size={12} aria-hidden />
                              </button>
                            </Floating>
                          )}
                        </td>
                      );
                    })}
                    <td className="hidden pl-2 text-right @sm:table-cell">{summary(row.habit)}</td>
                  </tr>
                  {/* editing gets a row of its own across the grid, so the name column keeps its width */}
                  {editing === row.id && (
                    <tr>
                      <td colSpan={shown.length + 2}>
                        <AlsoIn
                          here={ctx.workspaceId}
                          value={row.habit.linkWorkspaces}
                          readOnly={ro}
                          testId="habit-also-in"
                          onChange={(others) =>
                            ctx.write((doc) => setCounterWorkspaces(doc, row.id, ctx.workspaceId, others))
                          }
                        />
                        <HabitForm
                          initial={row.habit}
                          shared={shared}
                          onSave={(v) => {
                            ctx.write((doc) => updateCounter(doc, row.id, { ...v, goal: v.goal ?? 0 }));
                            setEditing(null);
                          }}
                          onArchive={() => {
                            ctx.write((doc) => updateCounter(doc, row.id, { archived: true }));
                            setEditing(null);
                          }}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ),
            )}
          </tbody>
        </table>
      )}

      {!ro && (
        <NewHabit
          shared={shared}
          onCreate={(v) =>
            ctx.write((doc) => void createCounter(doc, v.title, v.unit, { ...v, habit: true }))
          }
        />
      )}
    </div>
  );
}

interface HabitFields {
  title: string;
  unit: string;
  kind: CounterKind;
  goal?: number;
  period: CounterPeriod;
  scope?: CounterScope;
}

function HabitForm({
  initial,
  shared,
  onSave,
  onArchive,
  submitLabel,
}: {
  initial?: Partial<Counter>;
  /** a shared workspace: whether each person ticks for themselves can be chosen */
  shared?: boolean;
  onSave(v: HabitFields): void;
  onArchive?: () => void;
  submitLabel?: string;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(initial?.title ?? "");
  const [kind, setKind] = useState<CounterKind>(initial?.kind ?? "check");
  const [goal, setGoal] = useState(initial?.goal ? String(initial.goal) : "");
  const [period, setPeriod] = useState<CounterPeriod>(initial?.period ?? "day");
  const [unit, setUnit] = useState(initial?.unit ?? "");
  const [scope, setScope] = useState<CounterScope>(initial?.scope ?? "shared");
  const g = Number(goal);
  return (
    <form
      className="mt-2 flex flex-wrap items-end gap-2 font-normal"
      data-testid="habit-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!title.trim()) return;
        onSave({
          title: title.trim(),
          unit: unit.trim(),
          kind,
          goal: g > 0 ? g : undefined,
          period,
          ...(shared ? { scope } : {}),
        });
      }}
    >
      <Field label={t("habits.name")}>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          data-testid="habit-name"
          className="w-40"
        />
      </Field>
      <Field label={t("habits.kind")}>
        <Select
          value={kind}
          onChange={(e) => setKind(e.target.value as CounterKind)}
          data-testid="habit-kind"
        >
          <option value="check">{t("habits.kindCheck")}</option>
          <option value="count">{t("habits.kindCount")}</option>
        </Select>
      </Field>
      <Field label={t("habits.goal")}>
        <Input
          type="number"
          min={0}
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          className="w-20"
          data-testid="habit-goal"
        />
      </Field>
      <Field label={t("habits.period")}>
        <Select
          value={period}
          onChange={(e) => setPeriod(e.target.value as CounterPeriod)}
          data-testid="habit-period"
        >
          <option value="day">{t("habits.perDay")}</option>
          <option value="week">{t("habits.perWeek")}</option>
        </Select>
      </Field>
      {shared && (
        <Field label={t("habits.scope")}>
          <Select
            value={scope}
            onChange={(e) => setScope(e.target.value as CounterScope)}
            data-testid="habit-scope"
          >
            <option value="shared">{t("habits.scopeShared")}</option>
            <option value="perPerson">{t("habits.scopePerPerson")}</option>
          </Select>
        </Field>
      )}
      {kind === "count" && (
        <Field label={t("habits.unit")}>
          <Input value={unit} onChange={(e) => setUnit(e.target.value)} className="w-28" />
        </Field>
      )}
      <Button type="submit" size="sm" disabled={!title.trim()} data-testid="habit-save">
        {submitLabel ?? t("habits.save")}
      </Button>
      {onArchive && (
        <Button type="button" size="sm" variant="ghost" onClick={onArchive} data-testid="habit-archive">
          {t("habits.archive")}
        </Button>
      )}
    </form>
  );
}

function NewHabit({ onCreate, shared }: { onCreate(v: HabitFields): void; shared?: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState(0);
  if (!open)
    return (
      <div>
        <Button size="sm" variant="ghost" onClick={() => setOpen(true)} data-testid="habit-new">
          <Plus size={14} aria-hidden /> {t("habits.new")}
        </Button>
      </div>
    );
  return (
    <div className="border-t border-neutral-200 pt-2 dark:border-neutral-800">
      <HabitForm
        key={key}
        shared={shared}
        submitLabel={t("habits.create")}
        onSave={(v) => {
          onCreate(v);
          setKey(key + 1);
          setOpen(false);
        }}
      />
    </div>
  );
}

registerViewType<HabitsConfig>({
  id: "habits",
  label: "views.habits.label",
  icon: "calendar-check",
  level: "standard",
  defaultConfig: { display: "grid", range: "week", showRecurring: false },
  configSchema: {
    type: "object",
    properties: {
      display: { type: "string", title: "config.display", enum: ["grid", "tiles"] },
      range: { type: "string", title: "config.habitsRange", enum: ["week", "month"] },
      showRecurring: { type: "boolean", title: "config.showRecurring" },
      filter: FILTER_SCHEMA,
    },
  },
  Component: HabitsView,
  minSize: { w: 6, h: 5 },
});
